"use client";

import { useCallback, useEffect, useState } from "react";

type AutoplayOptions = {
  delay?: number;
  canScrollNext: boolean;
  scrollNext: () => void;
  scrollToIndex: (index: number) => void;
};

const useAutoplay = ({
  delay,
  canScrollNext,
  scrollNext,
  scrollToIndex,
}: AutoplayOptions) => {
  const [isPaused, setIsPaused] = useState(false);

  useEffect(() => {
    if (!delay) {
      return;
    }
    const handleVisibilityChange = () => {
      setIsPaused(document.visibilityState === "hidden");
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [delay]);

  useEffect(() => {
    if (!delay || isPaused) {
      return;
    }
    const timer = setInterval(() => {
      if (canScrollNext) {
        scrollNext();
        return;
      }
      scrollToIndex(0);
    }, delay);
    return () => clearInterval(timer);
  }, [delay, isPaused, canScrollNext, scrollNext, scrollToIndex]);

  const pause = useCallback(() => {
    if (delay) {
      setIsPaused(true);
    }
  }, [delay]);

  const resume = useCallback(() => {
    if (delay) {
      setIsPaused(false);
    }
  }, [delay]);

  return { pause, resume };
};

export default useAutoplay;
